import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import TemplateMonthView from './TemplateMonthView';
import TemplateDayView from './TemplateDayView';
import TemplateWeekView from './TemplateWeekView';
import { ConflictGroup } from '../../../_services/ConflictGroupService';

/* ── Reusable dropdown multiselect (fixed-position to escape overflow:hidden parents) ── */
interface MultiSelectOption {
  value: string;
  label: string;
  color?: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'Select...',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Position the fixed dropdown below the trigger
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 320;
      const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: Math.max(rect.width, 260),
        ...(openAbove
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        zIndex: 9999,
      });
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll/resize to avoid stale position
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    // capture scroll on any ancestor
    document.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = useCallback((value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter(v => v !== value)
        : [...selected, value]
    );
  }, [selected, onChange]);

  const remove = useCallback((value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== value));
  }, [selected, onChange]);

  const selectedLabels = useMemo(() => {
    const map = new Map(options.map(o => [o.value, o]));
    return selected.map(v => map.get(v)).filter(Boolean) as MultiSelectOption[];
  }, [selected, options]);

  return (
    <div className="tbev-msd">
      <div
        className={`tbev-msd-trigger ${open ? 'open' : ''}`}
        ref={triggerRef}
        onClick={() => { setOpen(!open); if (open) setSearch(''); }}
      >
        {selectedLabels.length === 0 ? (
          <span className="tbev-msd-placeholder">{placeholder}</span>
        ) : (
          <div className="tbev-msd-tags">
            {selectedLabels.slice(0, 2).map(opt => (
              <span
                key={opt.value}
                className="tbev-msd-tag"
                style={opt.color ? { background: opt.color, borderColor: opt.color } : undefined}
              >
                {opt.label}
                <span className="tbev-msd-tag-x" onClick={(e) => remove(opt.value, e)}>&times;</span>
              </span>
            ))}
            {selectedLabels.length > 2 && (
              <span className="tbev-msd-tag tbev-msd-tag-more">+{selectedLabels.length - 2}</span>
            )}
          </div>
        )}
        <span className={`tbev-msd-arrow ${open ? 'open' : ''}`}>&#9662;</span>
      </div>
      {open && (
        <div className="tbev-msd-dropdown" ref={dropdownRef} style={dropdownStyle}>
          <div className="tbev-msd-header">
            <input
              className="tbev-msd-search"
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {selected.length > 0 && (
              <span className="tbev-msd-selected-count">{selected.length} selected</span>
            )}
          </div>
          <div className="tbev-msd-options">
            {filtered.length === 0 && (
              <div className="tbev-msd-empty">No matches</div>
            )}
            {filtered.map(opt => (
              <label key={opt.value} className={`tbev-msd-option ${selected.includes(opt.value) ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                />
                {opt.color && (
                  <span className="tbev-msd-color-dot" style={{ background: opt.color }} />
                )}
                <span className="tbev-msd-option-label">{opt.label}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <button className="tbev-msd-clear" onClick={() => { onChange([]); setSearch(''); }}>
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds?: number[];
  attendanceTaken?: boolean;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
  substitution?: {
    id: number;
    status: string;
    substituteInstructorId: number | null;
    substituteInstructorName: string | null;
    substituteTopicId: number | null;
    substituteTopicName: string | null;
    substituteAgenda: string | null;
    reason: string | null;
  } | null;
}

interface BatchMapping {
  batchId: number;
  batchName: string;
  batchCode?: string;
}

interface TemplateBirdsEyeViewProps {
  slots: ActivitySlot[];
  templateId?: number;
  templateStartDate: string;
  templateEndDate: string;
  isEditMode: boolean;
  onAddSlot: (date?: string) => void;
  onEditSlot: (slot: ActivitySlot) => void;
  onDeleteSlot: (slotId: number) => void;
  onSlotMoved: () => void;
  templateBatchIds?: number[];
  batchMappings?: BatchMapping[];
  conflictGroups?: ConflictGroup[];
  onAttendance?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
  // Temporary parallel callback wired to the Node attendance route.
  // Remove when /attendance-node is fully validated.
  onAttendanceNode?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
  onSubstitute?: (slot: ActivitySlot) => void;
}

type ViewMode = 'month' | 'week' | 'day' | 'list';
type ListGroupBy = 'all' | 'instructor' | 'topic' | 'date' | 'batch';

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDateDisplay(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTimeDisplay(time: string): string {
  if (!time) return '-';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

function timeToMinutes(time: string): number {
  const parts = time.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

function getMonday(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

const TemplateBirdsEyeView: React.FC<TemplateBirdsEyeViewProps> = ({
  slots,
  templateId,
  templateStartDate,
  templateEndDate,
  isEditMode,
  onAddSlot,
  onEditSlot,
  onDeleteSlot,
  onSlotMoved,
  templateBatchIds = [],
  batchMappings = [],
  conflictGroups = [],
  onAttendance,
  onAttendanceNode,
  onSubstitute,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('month');

  const batchNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of batchMappings) {
      map.set(b.batchId, b.batchCode || b.batchName || `Batch ${b.batchId}`);
    }
    return map;
  }, [batchMappings]);

  const getBatchNames = (slot: ActivitySlot): string[] => {
    const ids = (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds;
    return ids.map(id => batchNameMap.get(id) || `Batch ${id}`);
  };

  const [listGroupBy, setListGroupBy] = useState<ListGroupBy>('all');
  // Seed the view from the earliest activity slot date so users land on the
  // month/week where actual classes begin, not on the template's start date
  // (which can be much earlier and show an empty view).
  const initialFocusDate = useMemo(() => {
    let earliest: string | null = null;
    for (const s of slots) {
      if (s.date && (!earliest || s.date < earliest)) earliest = s.date;
    }
    return earliest || templateStartDate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedDate, setSelectedDate] = useState<string>(initialFocusDate);
  const [weekStartDate, setWeekStartDate] = useState<string>(getMonday(initialFocusDate));
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const d = parseLocalDate(initialFocusDate);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterInstructors, setFilterInstructors] = useState<string[]>([]);
  const [filterTopic, setFilterTopic] = useState('');
  const [filterConflictGroups, setFilterConflictGroups] = useState<number[]>([]);
  const [filterDays, setFilterDays] = useState<number[]>([]);
  const exportRef = useRef<HTMLDivElement>(null);

  // Close export menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Derived filter options from all slots
  const instructorOptions = useMemo(() => {
    const names = slots.map(s => s.instructorName).filter(Boolean) as string[];
    return Array.from(new Set(names)).sort();
  }, [slots]);

  const topicOptions = useMemo(() => {
    const names = slots.map(s => s.topicName).filter(Boolean) as string[];
    return Array.from(new Set(names)).sort();
  }, [slots]);

  // Build set of instructor IDs for the selected conflict groups
  const conflictGroupInstructorIds = useMemo(() => {
    if (filterConflictGroups.length === 0) return null;
    const ids = new Set<number>();
    for (const groupId of filterConflictGroups) {
      const group = conflictGroups.find(g => g.id === groupId);
      if (!group?.members) continue;
      for (const member of group.members) {
        const input = member.input as any;
        if (input?.instructors && Array.isArray(input.instructors)) {
          for (const inst of input.instructors) {
            if (inst.instructorId) ids.add(inst.instructorId);
          }
        }
      }
    }
    return ids.size > 0 ? ids : null;
  }, [filterConflictGroups, conflictGroups]);

  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Count active filters for badge
  const activeFilterCount = [
    filterStartDate,
    filterEndDate,
    filterTopic,
  ].filter(Boolean).length
    + (filterInstructors.length > 0 ? 1 : 0)
    + (filterConflictGroups.length > 0 ? 1 : 0)
    + (filterDays.length > 0 ? 1 : 0);

  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterInstructors([]);
    setFilterTopic('');
    setFilterConflictGroups([]);
    setFilterDays([]);
    setSearchQuery('');
  };

  const toggleDay = (dayIndex: number) => {
    setFilterDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  // Filter slots by all criteria
  const filteredSlots = useMemo(() => {
    let result = slots;

    // Date range filter
    if (filterStartDate) {
      result = result.filter(s => s.date && s.date >= filterStartDate);
    }
    if (filterEndDate) {
      result = result.filter(s => s.date && s.date <= filterEndDate);
    }

    // Instructor filter (multiselect)
    if (filterInstructors.length > 0) {
      result = result.filter(s => s.instructorName && filterInstructors.includes(s.instructorName));
    }

    // Topic filter
    if (filterTopic) {
      result = result.filter(s => s.topicName === filterTopic);
    }

    // Conflict group filter (match by instructor IDs in the group's members)
    if (conflictGroupInstructorIds) {
      result = result.filter(s => s.instructorId && conflictGroupInstructorIds.has(s.instructorId));
    }

    // Day of week filter (0=Mon, 1=Tue, ..., 6=Sun)
    if (filterDays.length > 0) {
      result = result.filter(s => {
        if (!s.date) return false;
        const d = parseLocalDate(s.date);
        // JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat → convert to 0=Mon, ..., 6=Sun
        const jsDay = d.getDay();
        const dayIndex = jsDay === 0 ? 6 : jsDay - 1;
        return filterDays.includes(dayIndex);
      });
    }

    // Text search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        (s.instructorName && s.instructorName.toLowerCase().includes(q)) ||
        (s.topicName && s.topicName.toLowerCase().includes(q)) ||
        (s.agenda && s.agenda.toLowerCase().includes(q)) ||
        (s.date && s.date.includes(q))
      );
    }

    return result;
  }, [slots, filterStartDate, filterEndDate, filterInstructors, filterTopic, filterDays, searchQuery, conflictGroupInstructorIds]);

  // Analytics
  const analytics = useMemo(() => {
    const totalSlots = filteredSlots.length;
    let totalMinutes = 0;
    const instructorSet = new Set<number>();
    const topicSet = new Set<number>();
    const dateSet = new Set<string>();
    const instructorHours: Record<string, number> = {};
    const topicCounts: Record<string, number> = {};

    for (const slot of filteredSlots) {
      if (slot.startTime && slot.endTime) {
        const mins = timeToMinutes(slot.endTime) - timeToMinutes(slot.startTime);
        totalMinutes += mins;
        if (slot.instructorName) {
          instructorHours[slot.instructorName] = (instructorHours[slot.instructorName] || 0) + mins;
        }
      }
      if (slot.instructorId) instructorSet.add(slot.instructorId);
      if (slot.topicMasterId) topicSet.add(slot.topicMasterId);
      if (slot.date) dateSet.add(slot.date);
      if (slot.topicName) {
        topicCounts[slot.topicName] = (topicCounts[slot.topicName] || 0) + 1;
      }
    }

    // Date coverage: total days in range vs days with slots
    const start = parseLocalDate(templateStartDate);
    const end = parseLocalDate(templateEndDate);
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const instructorLoad = Object.entries(instructorHours)
      .map(([name, mins]) => ({ name, hours: Math.round(mins / 60 * 10) / 10 }))
      .sort((a, b) => b.hours - a.hours);

    const topicDistribution = Object.entries(topicCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    return {
      totalSlots,
      totalHours: Math.round(totalMinutes / 60 * 10) / 10,
      instructorCount: instructorSet.size,
      topicCount: topicSet.size,
      daysWithSlots: dateSet.size,
      totalDays,
      dateCoverage: totalDays > 0 ? Math.round((dateSet.size / totalDays) * 100) : 0,
      instructorLoad,
      topicDistribution,
    };
  }, [filteredSlots, templateStartDate, templateEndDate]);

  // Group slots for list view
  const groupedSlots = useMemo(() => {
    const groups: { key: string; label: string; slots: ActivitySlot[] }[] = [];

    const sortByDateTime = (a: ActivitySlot, b: ActivitySlot) =>
      (a.date || '').localeCompare(b.date || '') || (a.startTime || '').localeCompare(b.startTime || '');

    const sortByTime = (a: ActivitySlot, b: ActivitySlot) =>
      (a.startTime || '').localeCompare(b.startTime || '');

    if (listGroupBy === 'all') {
      groups.push({ key: 'all', label: 'All Slots', slots: [...filteredSlots].sort(sortByDateTime) });
    } else if (listGroupBy === 'date') {
      const byDate: Record<string, ActivitySlot[]> = {};
      for (const s of filteredSlots) {
        const key = s.date || 'No Date';
        (byDate[key] = byDate[key] || []).push(s);
      }
      Object.entries(byDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([date, slts]) => {
          groups.push({
            key: date,
            label: date === 'No Date' ? 'No Date' : formatDateDisplay(date),
            slots: slts.sort(sortByTime),
          });
        });
    } else if (listGroupBy === 'instructor') {
      const byInst: Record<string, ActivitySlot[]> = {};
      for (const s of filteredSlots) {
        const key = s.instructorName || 'Unassigned';
        (byInst[key] = byInst[key] || []).push(s);
      }
      Object.entries(byInst)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([name, slts]) => {
          groups.push({
            key: name,
            label: name,
            slots: slts.sort(sortByDateTime),
          });
        });
    } else if (listGroupBy === 'topic') {
      const byTopic: Record<string, ActivitySlot[]> = {};
      for (const s of filteredSlots) {
        const key = s.topicName || 'No Topic';
        (byTopic[key] = byTopic[key] || []).push(s);
      }
      Object.entries(byTopic)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([name, slts]) => {
          groups.push({
            key: name,
            label: name,
            slots: slts.sort(sortByDateTime),
          });
        });
    }

    return groups;
  }, [filteredSlots, listGroupBy]);

  // Navigation handlers
  const handleDayClick = (date: string) => {
    setSelectedDate(date);
    setViewMode('day');
  };

  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month);
  };

  const handleWeekChange = (weekStart: string) => {
    setWeekStartDate(weekStart);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  // Export handlers
  const exportCSV = () => {
    setShowExportMenu(false);
    const headers = ['Date', 'Day', 'Start Time', 'End Time', 'Instructor', 'Topic', 'Agenda'];
    const rows = filteredSlots
      .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
      .map(s => {
        const d = s.date ? parseLocalDate(s.date) : null;
        const dayName = d ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '';
        return [
          s.date || '',
          dayName,
          s.startTime || '',
          s.endTime || '',
          s.instructorName || '',
          s.topicName || '',
          s.agenda || '',
        ];
      });

    const csvContent = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template-slots.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportExcel = () => {
    setShowExportMenu(false);
    const wb = XLSX.utils.book_new();

    // Sheet 1: Slots
    const slotData = filteredSlots
      .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
      .map(s => ({
        Date: s.date || '',
        'Start Time': s.startTime || '',
        'End Time': s.endTime || '',
        Instructor: s.instructorName || '',
        Topic: s.topicName || '',
        Agenda: s.agenda || '',
      }));
    const slotsSheet = XLSX.utils.json_to_sheet(slotData);
    slotsSheet['!cols'] = [
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 40 },
    ];
    XLSX.utils.book_append_sheet(wb, slotsSheet, 'Slots');

    // Sheet 2: Analytics
    const analyticsData = [
      { Metric: 'Total Slots', Value: analytics.totalSlots },
      { Metric: 'Total Hours', Value: analytics.totalHours },
      { Metric: 'Instructors', Value: analytics.instructorCount },
      { Metric: 'Topics', Value: analytics.topicCount },
      { Metric: 'Days with Slots', Value: analytics.daysWithSlots },
      { Metric: 'Total Days in Range', Value: analytics.totalDays },
      { Metric: 'Date Coverage', Value: `${analytics.dateCoverage}%` },
    ];
    const analyticsSheet = XLSX.utils.json_to_sheet(analyticsData);
    analyticsSheet['!cols'] = [{ wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, analyticsSheet, 'Analytics');

    // Sheet 3: Instructor Load
    if (analytics.instructorLoad.length > 0) {
      const instSheet = XLSX.utils.json_to_sheet(
        analytics.instructorLoad.map(i => ({ Instructor: i.name, Hours: i.hours }))
      );
      instSheet['!cols'] = [{ wch: 30 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, instSheet, 'Instructor Load');
    }

    // Sheet 4: Topic Distribution
    if (analytics.topicDistribution.length > 0) {
      const topicSheet = XLSX.utils.json_to_sheet(
        analytics.topicDistribution.map(t => ({ Topic: t.name, 'Slot Count': t.count }))
      );
      topicSheet['!cols'] = [{ wch: 30 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, topicSheet, 'Topic Distribution');
    }

    XLSX.writeFile(wb, 'template-slots.xlsx');
  };

  const exportPDF = () => {
    setShowExportMenu(false);
    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(16);
    doc.text('Scheduled Classes', 14, 15);
    doc.setFontSize(10);
    doc.text(`Date Range: ${templateStartDate} to ${templateEndDate}`, 14, 22);
    doc.text(`Total: ${analytics.totalSlots} slots | ${analytics.totalHours} hours | ${analytics.instructorCount} instructors | ${analytics.topicCount} topics`, 14, 28);

    // Show active filters in PDF
    let yPos = 28;
    const activeFilters: string[] = [];
    if (filterStartDate || filterEndDate) {
      activeFilters.push(`Date: ${filterStartDate || templateStartDate} to ${filterEndDate || templateEndDate}`);
    }
    if (filterInstructors.length > 0) {
      activeFilters.push(`Instructors: ${filterInstructors.join(', ')}`);
    }
    if (filterTopic) {
      activeFilters.push(`Topic: ${filterTopic}`);
    }
    if (filterConflictGroups.length > 0) {
      const names = filterConflictGroups.map(id => conflictGroups.find(g => g.id === id)?.name).filter(Boolean);
      activeFilters.push(`Conflict Groups: ${names.join(', ')}`);
    }
    if (filterDays.length > 0) {
      activeFilters.push(`Days: ${filterDays.map(i => DAY_LABELS[i]).join(', ')}`);
    }
    if (activeFilters.length > 0) {
      yPos += 6;
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Filters: ${activeFilters.join(' | ')}`, 14, yPos);
      doc.setTextColor(0);
    }

    const tableData = filteredSlots
      .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
      .map(s => [
        s.date || '',
        s.startTime || '',
        s.endTime || '',
        s.instructorName || '',
        s.topicName || '',
        (s.agenda || '').substring(0, 50),
      ]);

    autoTable(doc, {
      head: [['Date', 'Start', 'End', 'Instructor', 'Topic', 'Agenda']],
      body: tableData,
      startY: yPos + 6,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [102, 126, 234] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 18 },
        2: { cellWidth: 18 },
        3: { cellWidth: 45 },
        4: { cellWidth: 55 },
        5: { cellWidth: 'auto' },
      },
    });

    doc.save('template-slots.pdf');
  };

  // Render analytics dashboard
  const renderAnalytics = () => (
    <div className="tbev-analytics">
      <div className="tbev-analytics-grid">
        <div className="tbev-analytics-card primary">
          <div className="analytics-value">{analytics.totalSlots}</div>
          <div className="analytics-label">Total Slots</div>
        </div>
        <div className="tbev-analytics-card success">
          <div className="analytics-value">{analytics.totalHours}h</div>
          <div className="analytics-label">Total Hours</div>
        </div>
        <div className="tbev-analytics-card info">
          <div className="analytics-value">{analytics.instructorCount}</div>
          <div className="analytics-label">Instructors</div>
        </div>
        <div className="tbev-analytics-card warning">
          <div className="analytics-value">{analytics.topicCount}</div>
          <div className="analytics-label">Topics</div>
        </div>
        <div className="tbev-analytics-card">
          <div className="analytics-value">{analytics.daysWithSlots}/{analytics.totalDays}</div>
          <div className="analytics-label">Days Covered ({analytics.dateCoverage}%)</div>
        </div>
      </div>

      <div className="tbev-analytics-details">
        {analytics.instructorLoad.length > 0 && (
          <div className="analytics-detail-panel">
            <h4>Instructor Load</h4>
            <div className="analytics-bar-list">
              {analytics.instructorLoad.slice(0, 8).map(inst => {
                const maxHours = analytics.instructorLoad[0]?.hours || 1;
                const pct = Math.round((inst.hours / maxHours) * 100);
                return (
                  <div key={inst.name} className="analytics-bar-item">
                    <span className="bar-label">{inst.name}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="bar-value">{inst.hours}h</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {analytics.topicDistribution.length > 0 && (
          <div className="analytics-detail-panel">
            <h4>Topic Distribution</h4>
            <div className="analytics-bar-list">
              {analytics.topicDistribution.slice(0, 8).map(topic => {
                const maxCount = analytics.topicDistribution[0]?.count || 1;
                const pct = Math.round((topic.count / maxCount) * 100);
                return (
                  <div key={topic.name} className="analytics-bar-item">
                    <span className="bar-label">{topic.name}</span>
                    <div className="bar-track">
                      <div className="bar-fill topic" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="bar-value">{topic.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render list view
  const renderListView = () => (
    <div className="tbev-list-view">
      <div className="tbev-list-groupby">
        {(['all', 'date', 'instructor', 'topic'] as ListGroupBy[]).map(mode => (
          <button
            key={mode}
            className={`tbev-groupby-btn ${listGroupBy === mode ? 'active' : ''}`}
            onClick={() => setListGroupBy(mode)}
          >
            {mode === 'all' ? 'All' : mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>

      {groupedSlots.map(group => (
        <div key={group.key} className="tbev-list-group">
          {listGroupBy !== 'all' && (
            <div className="tbev-list-group-header">
              <span className="group-title">{group.label}</span>
              <span className="group-count">{group.slots.length} slots</span>
            </div>
          )}
          <div className="tbev-list-cards">
            {group.slots.map(slot => {
              const sub = slot.substitution;
              const isApprovedSub = sub && sub.status === 'APPROVED';
              const isPendingSub = sub && sub.status === 'REQUESTED';
              const cardStyle = isApprovedSub
                ? { borderLeft: '3px solid #b8860b', background: '#fffdf6' }
                : isPendingSub
                  ? { borderLeft: '3px dashed #b8860b', background: '#fffdf6' }
                  : undefined;
              const effectiveInstructorName = isApprovedSub && sub?.substituteInstructorName
                ? sub.substituteInstructorName
                : slot.instructorName;
              const effectiveTopicName = isApprovedSub && sub?.substituteTopicName
                ? sub.substituteTopicName
                : slot.topicName;
              return (
              <div
                key={slot.id}
                className="tbev-slot-card"
                style={cardStyle}
                onClick={() => (isEditMode && !slot.attendanceTaken) ? onEditSlot(slot) : undefined}
              >
                <div className="tbev-card-time">
                  <div className="tbev-time-row">
                    <span className="time-display">{formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}</span>
                    {slot.attendanceTaken && (
                      <span className="attendance-done-badge" title="Attendance taken">A</span>
                    )}
                    {sub && (
                      <span
                        title={
                          isApprovedSub
                            ? `Substitution approved${sub.reason ? ` — ${sub.reason}` : ''}`
                            : 'Substitution request pending approval'
                        }
                        style={{
                          marginLeft: 6,
                          padding: '1px 6px',
                          borderRadius: 3,
                          background: isApprovedSub ? '#fff3cd' : '#fef3c7',
                          color: 'var(--status-warning-text)',
                          border: '1px solid #ffeeba',
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                        }}
                      >
                        {isApprovedSub ? 'SUB' : 'SUB?'}
                      </span>
                    )}
                  </div>
                  {listGroupBy !== 'date' && slot.date && (
                    <span className="date-display">{formatDateDisplay(slot.date)}</span>
                  )}
                </div>
                <div className="tbev-card-content">
                  {effectiveInstructorName && (
                    <div className="card-instructor">
                      {effectiveInstructorName}
                      {isApprovedSub && sub?.substituteInstructorName && slot.instructorName && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--status-warning-text)', fontStyle: 'italic' }}>
                          (was {slot.instructorName})
                        </span>
                      )}
                    </div>
                  )}
                  {effectiveTopicName && (
                    <div className="card-topic">
                      {effectiveTopicName}
                      {isApprovedSub && sub?.substituteTopicName && slot.topicName && sub.substituteTopicId !== slot.topicMasterId && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--status-warning-text)', fontStyle: 'italic' }}>
                          (was {slot.topicName})
                        </span>
                      )}
                    </div>
                  )}
                  {isApprovedSub && sub?.substituteAgenda && (
                    <div style={{ fontSize: 11, color: '#5d4037', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 3, padding: '3px 6px', marginTop: 4 }}>
                      <strong>Today's plan:</strong> {sub.substituteAgenda}
                    </div>
                  )}
                  {(slot.roomName || slot.centreName) && (
                    <div className="card-location">
                      {slot.roomName && <span>🏫 {slot.roomName}</span>}
                      {slot.centreName && <span style={{marginLeft: 6}}>📍 {slot.centreName}</span>}
                    </div>
                  )}
                  {batchMappings.length > 0 && (
                    <div className="card-batches">
                      {getBatchNames(slot).map((name, idx) => (
                        <span key={idx} className="slot-batch-tag">{name}</span>
                      ))}
                    </div>
                  )}
                  {slot.agenda && (
                    <div className="card-agenda">{slot.agenda}</div>
                  )}
                </div>
                <div className="tbev-card-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '12px' }}>
                  {/* Java attendance button hidden — Node attendance is now the canonical "Attendance" button.
                      Functionality preserved via onAttendance callback; only the button is hidden. */}
                  {/* {onAttendance && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                    <button className="card-attendance-btn" style={{ padding: '4px 8px', fontSize: '12px' }} title="Take Attendance (Java)" onClick={(e) => {
                      e.stopPropagation();
                      onAttendance(
                        (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                        slot.id,
                        slot.instructorId,
                        slot.instructorName
                      );
                    }}>Attendance</button>
                  )} */}
                  {onAttendanceNode && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                    <button
                      className="card-attendance-btn"
                      title="Take Attendance"
                      style={{ background: 'var(--brand-primary)', color: 'var(--text-on-brand)', borderColor: 'var(--brand-primary)', padding: '4px 8px', fontSize: '12px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAttendanceNode(
                          (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                          slot.id,
                          slot.instructorId,
                          slot.instructorName
                        );
                      }}
                    >
                      Attendance
                    </button>
                  )}
                  {onSubstitute && (
                    <button
                      className="card-substitute-btn"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                      onClick={(e) => { e.stopPropagation(); onSubstitute(slot); }}
                      title="Assign substitute teacher for this slot"
                    >
                      Substitute
                    </button>
                  )}
                  {isEditMode && !slot.attendanceTaken && (
                    <>
                      <button className="card-edit-btn" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={(e) => { e.stopPropagation(); onEditSlot(slot); }}>Edit</button>
                      <button className="card-delete-btn" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={(e) => { e.stopPropagation(); onDeleteSlot(slot.id); }}>Delete</button>
                    </>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      ))}

      {filteredSlots.length === 0 && (
        <div className="tbev-empty">No slots to display</div>
      )}
    </div>
  );

  return (
    <div className="template-bev">
      {/* Toolbar */}
      <div className="tbev-toolbar">
        <div className="tbev-view-selector">
          {(['month', 'week', 'day', 'list'] as ViewMode[]).map(mode => (
            <button
              key={mode}
              className={`tbev-view-btn ${viewMode === mode ? 'active' : ''}`}
              onClick={() => setViewMode(mode)}
            >
              {mode === 'month' && 'Month'}
              {mode === 'week' && 'Week'}
              {mode === 'day' && 'Day'}
              {mode === 'list' && 'List'}
            </button>
          ))}
        </div>

        <div className="tbev-actions">
          <input
            type="text"
            className="tbev-search"
            placeholder="Search slots..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            className={`tbev-filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="tbev-filter-badge">{activeFilterCount}</span>
            )}
          </button>
          <button
            className={`tbev-analytics-toggle ${showAnalytics ? 'active' : ''}`}
            onClick={() => setShowAnalytics(!showAnalytics)}
          >
            Analytics
          </button>
          <div className="tbev-export-wrapper" ref={exportRef}>
            <button
              className="tbev-export-btn"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              Export
            </button>
            {showExportMenu && (
              <div className="tbev-export-menu">
                <button onClick={exportCSV}>CSV</button>
                <button onClick={exportExcel}>Excel</button>
                <button onClick={exportPDF}>PDF</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      <div className={`tbev-filters-panel ${showFilters ? 'visible' : ''}`}>
        <div className="tbev-filters-grid">
          <div className="tbev-filter-item">
            <label>Start Date</label>
            <input
              type="date"
              className="tbev-filter-input"
              value={filterStartDate}
              min={templateStartDate}
              max={templateEndDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>
          <div className="tbev-filter-item">
            <label>End Date</label>
            <input
              type="date"
              className="tbev-filter-input"
              value={filterEndDate}
              min={filterStartDate || templateStartDate}
              max={templateEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>
          <div className="tbev-filter-item">
            <label>Instructor</label>
            <MultiSelectDropdown
              options={instructorOptions.map(name => ({ value: name, label: name }))}
              selected={filterInstructors}
              onChange={setFilterInstructors}
              placeholder="All Instructors"
            />
          </div>
          <div className="tbev-filter-item">
            <label>Topic</label>
            <select
              className="tbev-filter-select"
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
            >
              <option value="">All Topics</option>
              {topicOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          {conflictGroups.length > 0 && (
            <div className="tbev-filter-item">
              <label>Conflict Group</label>
              <MultiSelectDropdown
                options={conflictGroups.map(g => ({ value: String(g.id), label: g.name, color: g.color || undefined }))}
                selected={filterConflictGroups.map(String)}
                onChange={(vals) => setFilterConflictGroups(vals.map(Number))}
                placeholder="All Conflict Groups"
              />
            </div>
          )}
          <div className="tbev-filter-item tbev-filter-days-item">
            <label>Days</label>
            <div className="tbev-filter-days">
              {DAY_LABELS.map((label, i) => (
                <label key={i} className={`tbev-day-chip ${filterDays.includes(i) ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={filterDays.includes(i)}
                    onChange={() => toggleDay(i)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="tbev-filter-actions">
            {activeFilterCount > 0 && (
              <button className="tbev-clear-filters" onClick={clearFilters}>
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && renderAnalytics()}

      {/* Active View */}
      <div className="tbev-content">
        {viewMode === 'month' && (
          <TemplateMonthView
            slots={filteredSlots}
            currentMonth={currentMonth}
            templateStartDate={templateStartDate}
            templateEndDate={templateEndDate}
            isEditMode={isEditMode}
            onMonthChange={handleMonthChange}
            onDayClick={handleDayClick}
            onAddSlot={onAddSlot}
            onEditSlot={onEditSlot}
            onDeleteSlot={onDeleteSlot}
            templateBatchIds={templateBatchIds}
            batchMappings={batchMappings}
            onAttendance={onAttendance}
            onAttendanceNode={onAttendanceNode}
          />
        )}

        {viewMode === 'week' && (
          <TemplateWeekView
            slots={filteredSlots}
            weekStartDate={weekStartDate}
            templateStartDate={templateStartDate}
            templateEndDate={templateEndDate}
            templateId={templateId}
            isEditMode={isEditMode}
            onWeekChange={handleWeekChange}
            onDayClick={handleDayClick}
            onAddSlot={onAddSlot}
            onEditSlot={onEditSlot}
            onDeleteSlot={onDeleteSlot}
            onSlotMoved={onSlotMoved}
            templateBatchIds={templateBatchIds}
            batchMappings={batchMappings}
            onAttendance={onAttendance}
            onAttendanceNode={onAttendanceNode}
          />
        )}

        {viewMode === 'day' && (
          <TemplateDayView
            slots={filteredSlots.filter(s => s.date === selectedDate)}
            date={selectedDate}
            templateStartDate={templateStartDate}
            templateEndDate={templateEndDate}
            templateId={templateId}
            isEditMode={isEditMode}
            onDateChange={handleDateChange}
            onAddSlot={onAddSlot}
            onEditSlot={onEditSlot}
            onDeleteSlot={onDeleteSlot}
            onSlotMoved={onSlotMoved}
            templateBatchIds={templateBatchIds}
            batchMappings={batchMappings}
            onAttendance={onAttendance}
            onAttendanceNode={onAttendanceNode}
          />
        )}

        {viewMode === 'list' && renderListView()}
      </div>
    </div>
  );
};

export default TemplateBirdsEyeView;
